import React, { useState } from 'react';
import { cn, getSeverityColor } from '@/lib/utils';
import { 
  Bug, ExternalLink, Shield, AlertTriangle, CheckCircle, 
  Copy, Server, Calendar, Target, Lightbulb, FileText
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent } from '@/components/ui/card';

// Remediation steps based on CVE
const getRemediationSteps = (vuln) => {
  const genericSteps = [
    { step: 1, title: 'Identificar sistemas afetados', description: 'Realizar scan para identificar todos os ativos com a vulnerabilidade.' },
    { step: 2, title: 'Avaliar impacto no negócio', description: 'Determinar criticidade dos sistemas afetados para priorização.' },
    { step: 3, title: 'Aplicar patch ou mitigação', description: 'Instalar atualização de segurança do vendor ou aplicar mitigação.' },
    { step: 4, title: 'Validar correção', description: 'Executar novo scan para confirmar que a vulnerabilidade foi corrigida.' },
    { step: 5, title: 'Documentar ação', description: 'Registrar todas as ações tomadas para compliance e auditoria.' },
  ];
  
  return genericSteps;
};

// Additional CVE details
const getCVEDetails = (cve_id) => {
  const details = {
    'CVE-2024-3400': {
      vendor: 'Palo Alto Networks',
      product: 'PAN-OS',
      affected_versions: '10.2, 11.0, 11.1',
      patch_available: true,
      patch_version: '10.2.9-h1, 11.0.4-h1, 11.1.2-h3',
      workaround: 'Desabilitar device telemetry se não puder aplicar patch imediatamente.',
      references: [
        'https://security.paloaltonetworks.com/CVE-2024-3400',
        'https://nvd.nist.gov/vuln/detail/CVE-2024-3400'
      ]
    },
    'CVE-2024-21762': {
      vendor: 'Fortinet',
      product: 'FortiOS SSL VPN',
      affected_versions: '6.0 - 7.4.2',
      patch_available: true,
      patch_version: '7.4.3, 7.2.7, 7.0.14',
      workaround: 'Desabilitar SSL VPN se não for essencial. Restringir acesso por IP.',
      references: [
        'https://www.fortiguard.com/psirt/FG-IR-24-015',
        'https://nvd.nist.gov/vuln/detail/CVE-2024-21762'
      ]
    },
    'CVE-2023-4966': {
      vendor: 'Citrix',
      product: 'NetScaler ADC/Gateway',
      affected_versions: '13.0, 13.1, 14.1',
      patch_available: true,
      patch_version: '13.0-92.19, 13.1-49.15, 14.1-8.50',
      workaround: 'Restringir acesso ao management interface. Monitorar sessões ativas.',
      references: [
        'https://support.citrix.com/article/CTX579459',
        'https://nvd.nist.gov/vuln/detail/CVE-2023-4966'
      ]
    },
    'CVE-2024-0012': {
      vendor: 'Palo Alto Networks',
      product: 'PAN-OS',
      affected_versions: '10.2, 11.0, 11.1, 11.2',
      patch_available: true,
      patch_version: '11.2.4-h1, 11.1.5-h1, 11.0.6-h1',
      workaround: 'Restringir acesso ao management interface apenas para IPs confiáveis.',
      references: [
        'https://security.paloaltonetworks.com/CVE-2024-0012',
        'https://nvd.nist.gov/vuln/detail/CVE-2024-0012'
      ]
    }
  };
  
  return details[cve_id] || {
    vendor: 'Verificar NVD',
    product: 'N/A',
    affected_versions: 'Consultar advisory',
    patch_available: null,
    patch_version: 'Consultar vendor',
    workaround: 'Consultar advisory do vendor para mitigações.',
    references: [`https://nvd.nist.gov/vuln/detail/${cve_id}`]
  };
};

export function TopVulnerabilities({ vulnerabilities = [] }) {
  const [selectedVuln, setSelectedVuln] = useState(null);
  const topVulns = vulnerabilities.slice(0, 5);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const getCVSSColor = (score) => {
    if (score >= 9.0) return 'text-destructive';
    if (score >= 7.0) return 'text-orange-500';
    if (score >= 4.0) return 'text-warning';
    return 'text-info';
  };

  return (
    <div className="space-y-4" data-testid="top-vulnerabilities">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-sm uppercase tracking-wider text-muted-foreground">
          Top Vulnerabilidades
        </h3>
        <Badge variant="outline" className="font-mono">
          {vulnerabilities.length}
        </Badge>
      </div>
      
      <ScrollArea className="h-[390px]">
        <div className="space-y-2">
          {topVulns.map((vuln) => (
            <div
              key={vuln.id}
              onClick={() => setSelectedVuln(vuln)}
              className="bg-secondary/50 rounded-sm p-3 border border-border hover:border-warning/50 transition-all group cursor-pointer"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-start gap-2">
                  <Bug className="h-4 w-4 mt-0.5 text-warning flex-shrink-0" />
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-mono text-info">{vuln.cve_id}</span>
                      <Badge className={cn("text-xs border", getSeverityColor(vuln.severity))}>
                        {vuln.cvss_score}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground line-clamp-1">{vuln.title}</p>
                  </div>
                </div>
                <ExternalLink className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            </div>
          ))}
        </div>
      </ScrollArea>

      {/* Vulnerability Detail Modal */}
      <Dialog open={!!selectedVuln} onOpenChange={() => setSelectedVuln(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <Bug className="h-6 w-6 text-warning" />
              <span className="font-mono">{selectedVuln?.cve_id}</span>
              <Badge className={cn("border", getSeverityColor(selectedVuln?.severity))}>
                CVSS {selectedVuln?.cvss_score}
              </Badge>
            </DialogTitle>
          </DialogHeader>
          
          {selectedVuln && (
            <Tabs defaultValue="overview" className="mt-4">
              <TabsList className="bg-secondary">
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="remediation">Remediação</TabsTrigger>
                <TabsTrigger value="affected">Assets Afetados</TabsTrigger>
                <TabsTrigger value="references">Referências</TabsTrigger>
              </TabsList>
              
              <ScrollArea className="h-[450px] mt-4">
                <TabsContent value="overview" className="space-y-4 mt-0">
                  <Card className="bg-secondary/50">
                    <CardContent className="pt-4">
                      <h4 className="font-display text-xs uppercase text-muted-foreground mb-2">Descrição</h4>
                      <p className="text-sm">{selectedVuln.description}</p>
                    </CardContent>
                  </Card>

                  <div className="grid grid-cols-2 gap-4">
                    <Card className="bg-secondary/50">
                      <CardContent className="pt-4">
                        <h4 className="font-display text-xs uppercase text-muted-foreground mb-3">Detalhes do CVE</h4>
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span className="text-sm text-muted-foreground">Severidade</span>
                            <Badge className={cn("border", getSeverityColor(selectedVuln.severity))}>
                              {selectedVuln.severity.toUpperCase()}
                            </Badge>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-muted-foreground">CVSS Score</span>
                            <span className={cn("font-mono font-bold", getCVSSColor(selectedVuln.cvss_score))}>
                              {selectedVuln.cvss_score}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-muted-foreground">Status</span>
                            <Badge variant={selectedVuln.status === 'patched' ? 'default' : 'destructive'}>
                              {selectedVuln.status}
                            </Badge>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="bg-secondary/50">
                      <CardContent className="pt-4">
                        <h4 className="font-display text-xs uppercase text-muted-foreground mb-3">Informações do Vendor</h4>
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span className="text-sm text-muted-foreground">Vendor</span>
                            <span className="font-mono text-sm">{getCVEDetails(selectedVuln.cve_id).vendor}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-muted-foreground">Produto</span>
                            <span className="font-mono text-sm">{getCVEDetails(selectedVuln.cve_id).product}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-muted-foreground">Versões Afetadas</span>
                            <span className="font-mono text-sm">{getCVEDetails(selectedVuln.cve_id).affected_versions}</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {getCVEDetails(selectedVuln.cve_id).patch_available && (
                    <Card className="bg-success/10 border-success/30">
                      <CardContent className="pt-4">
                        <h4 className="font-display text-xs uppercase text-success mb-2 flex items-center gap-2">
                          <CheckCircle className="h-4 w-4" /> Patch Disponível
                        </h4>
                        <p className="text-sm">
                          Versão corrigida: <span className="font-mono font-bold">{getCVEDetails(selectedVuln.cve_id).patch_version}</span>
                        </p>
                      </CardContent>
                    </Card>
                  )}

                  <Card className="bg-warning/10 border-warning/30">
                    <CardContent className="pt-4">
                      <h4 className="font-display text-xs uppercase text-warning mb-2 flex items-center gap-2">
                        <AlertTriangle className="h-4 w-4" /> Workaround
                      </h4>
                      <p className="text-sm">{getCVEDetails(selectedVuln.cve_id).workaround}</p>
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="remediation" className="space-y-4 mt-0">
                  <Card className="bg-primary/10 border-primary/30">
                    <CardContent className="pt-4">
                      <h4 className="font-display text-xs uppercase text-primary mb-2 flex items-center gap-2">
                        <Lightbulb className="h-4 w-4" /> Plano de Remediação
                      </h4>
                      <p className="text-sm">
                        Siga os passos abaixo para remediar esta vulnerabilidade de forma segura e documentada.
                      </p>
                    </CardContent>
                  </Card>

                  {getRemediationSteps(selectedVuln).map((step) => (
                    <Card key={step.step} className="bg-secondary/50">
                      <CardContent className="pt-4">
                        <div className="flex items-start gap-4">
                          <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-primary font-bold">
                            {step.step}
                          </div>
                          <div>
                            <h5 className="font-medium">{step.title}</h5>
                            <p className="text-sm text-muted-foreground mt-1">{step.description}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}

                  <div className="flex gap-2 pt-4">
                    <Button className="flex-1">
                      <Target className="h-4 w-4 mr-2" />
                      Criar Ticket de Remediação
                    </Button>
                    <Button variant="outline">
                      <FileText className="h-4 w-4 mr-2" />
                      Exportar Plano
                    </Button>
                  </div>
                </TabsContent>

                <TabsContent value="affected" className="space-y-4 mt-0">
                  <Card className="bg-secondary/50">
                    <CardContent className="pt-4">
                      <h4 className="font-display text-xs uppercase text-muted-foreground mb-3 flex items-center gap-2">
                        <Server className="h-4 w-4" /> Assets Afetados
                      </h4>
                      {selectedVuln.affected_assets?.length > 0 ? (
                        <div className="space-y-2">
                          {selectedVuln.affected_assets.map((asset, i) => (
                            <div key={i} className="flex items-center justify-between p-2 bg-background rounded">
                              <div className="flex items-center gap-2">
                                <Server className="h-4 w-4 text-muted-foreground" />
                                <span className="font-mono text-sm">{asset}</span>
                              </div>
                              <Badge variant="destructive">Vulnerável</Badge>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-8 text-muted-foreground">
                          <Shield className="h-8 w-8 mx-auto mb-2 text-success" />
                          <p className="text-sm">Nenhum asset específico listado</p>
                          <p className="text-xs mt-1">Execute um scan para identificar assets afetados</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  <Button variant="outline" className="w-full">
                    <Target className="h-4 w-4 mr-2" />
                    Executar Scan de Vulnerabilidade
                  </Button>
                </TabsContent>

                <TabsContent value="references" className="space-y-4 mt-0">
                  <Card className="bg-secondary/50">
                    <CardContent className="pt-4">
                      <h4 className="font-display text-xs uppercase text-muted-foreground mb-3">Links de Referência</h4>
                      <div className="space-y-2">
                        {getCVEDetails(selectedVuln.cve_id).references.map((ref, i) => (
                          <a
                            key={i}
                            href={ref}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 p-2 bg-background rounded hover:bg-primary/10 transition-colors"
                          >
                            <ExternalLink className="h-4 w-4 text-info" />
                            <span className="text-sm text-info truncate">{ref}</span>
                          </a>
                        ))}
                        
                        <a
                          href={`https://nvd.nist.gov/vuln/detail/${selectedVuln.cve_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 p-2 bg-background rounded hover:bg-primary/10 transition-colors"
                        >
                          <ExternalLink className="h-4 w-4 text-info" />
                          <span className="text-sm text-info">NVD - National Vulnerability Database</span>
                        </a>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="bg-secondary/50">
                    <CardContent className="pt-4">
                      <h4 className="font-display text-xs uppercase text-muted-foreground mb-3">Copiar CVE ID</h4>
                      <div className="flex items-center gap-2">
                        <code className="flex-1 bg-background p-2 rounded font-mono">{selectedVuln.cve_id}</code>
                        <Button size="icon" variant="ghost" onClick={() => copyToClipboard(selectedVuln.cve_id)}>
                          <Copy className="h-4 w-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>
              </ScrollArea>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default TopVulnerabilities;
